'use client';

import { useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { formatCurrency, parseCurrency, toCents } from './currency';
import { sanitizeDecimalInput } from './decimalInput';
import { mapFieldValues } from './types';
import type { CalculatorField, CalculatorFieldValues } from './types';

export const useCurrencyFields = (
  initialValues: CalculatorFieldValues,
  /** Override where the field shows its own `$`, so blur cannot render `$ $1,000.00`. */
  formatValue: (value: number) => string = formatCurrency,
) => {
  const [values, setValues] = useState<CalculatorFieldValues>(initialValues);
  // Enter submits without blurring, so a commit has to know which field is still mid-edit.
  const focusedLabel = useRef<string | null>(null);

  /** Rounds to whole cents, so a commit and a blur can never disagree on what `999999.004` is worth. */
  const normalizeValue = (raw: string, shouldFormat: boolean) => {
    if (raw === '') return '';

    const value = toCents(parseCurrency(raw)) / 100;

    return shouldFormat ? formatValue(value) : String(value);
  };

  const updateValue = (label: string, transform: (raw: string) => string) =>
    setValues(previous => ({ ...previous, [label]: transform(previous[label]) }));

  const handleChange = (label: string, value: string) => updateValue(label, () => sanitizeDecimalInput(value));

  const handleFocus = (label: string) => {
    focusedLabel.current = label;
    updateValue(label, raw => normalizeValue(raw, false));
  };

  const handleBlur = (label: string) => {
    focusedLabel.current = null;
    updateValue(label, raw => normalizeValue(raw, true));
  };

  const normalizeAll = (formatFocusedField: boolean): CalculatorFieldValues =>
    mapFieldValues(values, (raw, label) => normalizeValue(raw, formatFocusedField || focusedLabel.current !== label));

  const fieldProps = (field: CalculatorField, error?: string) => ({
    label: field.label,
    hint: field.hint,
    inputMode: 'decimal' as const,
    value: values[field.label],
    error,
    invalid: Boolean(error),
    onChange: (event: ChangeEvent<HTMLInputElement>) => handleChange(field.label, event.target.value),
    onFocus: () => handleFocus(field.label),
    onBlur: () => handleBlur(field.label),
  });

  return {
    values,
    /** What Calculate should read. Writes nothing, so the caller can validate before deciding how to `commit`. */
    normalize: () => normalizeAll(false),
    /** Keeps a focused field unformatted unless the form is about to be replaced. */
    commit: ({ formatFocusedField = false } = {}) => setValues(normalizeAll(formatFocusedField)),
    reset: () => {
      focusedLabel.current = null;
      setValues(initialValues);
    },
    fieldProps,
  };
};

/** Lets a form component take the factory without restating `CalculatorInput`'s props. */
export type CurrencyFieldProps = ReturnType<typeof useCurrencyFields>['fieldProps'];
